import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationManagerService } from 'src/app/core';
import { PriceListInterface } from '../utils/item.interface';
import { leanPMS } from 'app/ajs-upgraded-providers';

import {
  LeanPMSApiService,
  LeanPMSChainProduct
} from '../../digital-payments';
import { AlternativeTranslate, Vat } from 'tilby-models';
import { TranslateFieldDialogService } from 'src/app/dialogs/translate-field-dialog/translate-field-dialog.component';

export interface TranslationsInputData {
  mainValue: string,
  currentValues: AlternativeTranslate[],
  maxLength: number
}

const MAX_PRICE_LISTS = 10;

@Injectable({
  providedIn: 'root'
})
export class ItemDataOperationsService {
  private readonly configurationManager = inject(ConfigurationManagerService);
  private readonly leanPMS = inject(leanPMS);
  private readonly leanPMSApi = inject(LeanPMSApiService);
  private readonly translate = inject(TranslateService);
  private readonly translateFieldDialogService = inject(TranslateFieldDialogService);
  
  isLeanPMSEnabled: boolean | null = null;
  chainProductsLeanPMS: Array<LeanPMSChainProduct> | null = null;

  /**
   * Retrieves the price lists.
   * 
   * @param {object} options - The options for retrieving the price lists.
   * @param {boolean} [options.firstRequired=true] - Whether the first price list is required.
   * @param {boolean} [options.includeHidden=false] - Whether to include hidden price lists.
   * @returns {PriceListInterface[]} The price lists configured in the system.
   */
  getPriceLists(options?: { firstRequired?: boolean, includeHidden?: boolean }): PriceListInterface[] {
    const priceLists: PriceListInterface[] = [];

    for (let idx = 1; idx <= MAX_PRICE_LISTS; idx++) {
      const priceListName = this.configurationManager.getPreference(`price_list_${idx}_name`) ||
        `${this.translate.instant('ITEMS.DETAILS.PRICE_LIST_START')} ${idx}`;

      priceLists.push({
        index: idx,
        departmentLabel: `${this.translate.instant('ITEMS.DETAILS.DEPARTMENT')} ${priceListName}`,
        departmentFieldName: idx === 1 ? 'department_id' : `department${idx}_id`,
        hidden: !!(idx !== 1 && this.configurationManager.getPreference(`price_list_${idx}_hide`)),
        priceFieldName: `price${idx}`,
        value: priceListName,
        required: idx === 1 ? !!(options?.firstRequired) : false
      });
    }

    return options?.includeHidden
      ? priceLists
      : priceLists.filter(priceList => !priceList.hidden);
  }

  /**
   * Checks if LeanPMS is enabled.
   * 
   * @returns {Promise<boolean>} A promise that resolves to a boolean indicating whether LeanPMS is enabled.
   */
  async isLeanPmsEnabled() {
    if(this.isLeanPMSEnabled == null) {
        this.isLeanPMSEnabled = await this.leanPMS.isSetup();
    }
    return this.isLeanPMSEnabled;
  }

  /**
   * Resets the LeanPMS enabled status.
   */
  resetIsLeanPmsEnabled() {
    this.isLeanPMSEnabled = null;
  }

  /**
   * Fetches the chain products from LeanPMS.
   * 
   * @returns {Promise<Array<LeanPMSChainProduct> | null>} A promise that resolves to an array of chain products or null if an error occurs.
   */
  async fetchChainProducts() {
    if(this.chainProductsLeanPMS == null) {
        try {
            const data = await this.leanPMSApi.getChainProducts();
            this.chainProductsLeanPMS = data.results;
        } catch(error) {
            console.log(error);
        }
    }
    return this.chainProductsLeanPMS;
  }

  /**
   * Resets the chain products.
   */
  resetChainProduction() {
    this.chainProductsLeanPMS = null;
  }

  /**
   * Gets the department VAT.
   * 
   * @param {Vat} [vat] - The VAT.
   * @returns {string} The department VAT.
   */
  getDepartmentVat(vat?: Vat) {
    if (!vat) {
      return '';
    }

    return (!vat.value && vat.code)
      ? `(${this.translate.instant(`ITEMS.DEPARTMENTS.VAT_EXEMPTION_${vat.code}`)})`
      : `(${this.translate.instant('ITEMS.DETAILS.VAT', { value: vat.value })})`;
  }

  /**
   * Apre la modale di gestione multilingua e restituisce l'array già mappato e unito ai valori precedenti
   */
  async getUpdatedTranslations(config: TranslationsInputData): Promise<AlternativeTranslate[] | null> {
    const dialogResult = await this.translateFieldDialogService.openDialog({
      data: {
        mainValue: config.mainValue,
        alternative_names: [...config.currentValues].map(v => ({ ...v })),
        nameMaxLength: config.maxLength
      }
    });

    if (!dialogResult || !dialogResult.length) {
      return null;
    }

    const updatedValues: AlternativeTranslate[] = [...config.currentValues].map(v => ({ ...v }));

    dialogResult.forEach((translation: any) => {
      const index = updatedValues.findIndex(tr => tr.iso_language === translation.iso_code);

      if (index !== -1) {
        updatedValues[index].value = translation.valueField;
      } else {
        updatedValues.push({
          iso_language: translation.iso_code,
          value: translation.valueField
        });
      }
    });

    return updatedValues;
  }
}
